import { useEffect, useState } from 'react';
import api from '../lib/api';
import { X, PaperPlaneTilt, Spinner, EnvelopeSimple } from '@phosphor-icons/react';

/**
 * Compose & send a real email to a contact, then auto-log it to History.
 *
 * Props:
 *   open: boolean
 *   contact: { contact_id, first_name, last_name, email, salutation, company, ... }
 *   onClose: () => void
 *   onSent: (activity) => void   // optional, lets caller refresh history
 */
export default function ComposeEmailDialog({ open, contact, onClose, onSent, initialSubject = '', initialBody = '' }) {
  const [emailStatus, setEmailStatus] = useState({ configured: false, gmail_connected: false });
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [provider, setProvider] = useState('resend');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [e, t] = await Promise.all([
          api.get('/email/status'),
          api.get('/templates'),
        ]);
        setEmailStatus(e.data || { configured: false });
        // Default to Gmail when connected — better deliverability for one-off
        setProvider(e.data?.gmail_connected ? 'gmail' : 'resend');
        // Only email-type templates make sense here
        setTemplates((t.data || []).filter((tpl) => tpl.template_type === 'email'));
      } catch (err) {
        console.error(err);
      }
    })();
  }, [open]);

  // Reset whenever a different contact is loaded
  useEffect(() => {
    if (open) {
      setSubject(initialSubject || '');
      setBody(initialBody || '');
      setTemplateId('');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.contact_id]);

  if (!open || !contact) return null;

  // Lenient email regex — catches obvious typos like missing @ or trailing dot
  // without rejecting unusual-but-valid addresses (e.g., +tags, dashes).
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const contactEmail = (contact.email || '').trim();
  const emailLooksValid = EMAIL_RE.test(contactEmail);

  const applyMerge = (text) => {
    if (!text) return '';
    const map = {
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      salutation: contact.salutation || contact.first_name || '',
      company: contact.company || '',
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      address_street: contact.address_street || '',
      address_city: contact.address_city || '',
      address_state: contact.address_state || '',
      address_zip: contact.address_zip || '',
      status: contact.status || '',
      date: new Date().toLocaleDateString(),
    };
    return text.replace(/\{(\w+)\}/g, (_, k) => map[k] ?? `{${k}}`);
  };

  const onTemplateChange = (id) => {
    setTemplateId(id);
    if (!id) return;
    const tpl = templates.find((t) => t.template_id === id);
    if (!tpl) return;
    setSubject(applyMerge(tpl.subject || ''));
    setBody(applyMerge(tpl.body || ''));
  };

  const send = async () => {
    setError('');
    if (!contactEmail) {
      setError('This contact has no email address. Add one on the Business Card, then try again.');
      return;
    }
    if (!emailLooksValid) {
      setError(
        `"${contactEmail}" doesn't look like a valid email address. ` +
        `Update the contact's Email field on the Business Card (it should look like name@domain.com) and try again.`
      );
      return;
    }
    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }
    if (!body.trim()) {
      setError('Message body is required.');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/email/send', {
        to: contact.email,
        subject: subject.trim(),
        body,
        contact_id: contact.contact_id,
        provider,
      });
      // The backend already auto-logged the activity. Hand it back to the parent.
      onSent?.({
        activity_type: 'email',
        title: subject.trim(),
        content: body,
        email_provider: provider,
        email_provider_id: data.id,
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  const providerReady = provider === 'gmail' ? emailStatus.gmail_connected : emailStatus.configured;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="compose-email-dialog">
      <div
        className="bg-white border border-[#999] shadow-2xl w-full max-w-[640px] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ACT! orange header */}
        <div className="bg-gradient-to-b from-[#F39C12] to-[#D68910] px-4 py-2 border-b border-[#999] flex items-center justify-between shrink-0">
          <h2 className="text-[13px] font-bold text-white flex items-center gap-2">
            <EnvelopeSimple size={14} weight="bold" />
            Compose Email
          </h2>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded p-1" data-testid="compose-close-btn">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {/* To */}
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#666] w-[60px] shrink-0">To</label>
            <div className="flex-1 text-[12px] bg-[#F5F5F0] border border-[#CCC] px-2 py-1.5 text-[#333]" data-testid="compose-to">
              {contact.first_name || contact.last_name ? <strong>{`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}</strong> : null}
              {contactEmail ? (
                <span className={`ml-2 ${emailLooksValid ? 'text-[#666]' : 'text-[#C0392B] font-semibold'}`}>
                  &lt;{contactEmail}&gt;
                </span>
              ) : (
                <span className="text-red-600 ml-2">no email on file</span>
              )}
            </div>
          </div>
          {contactEmail && !emailLooksValid && (
            <div className="ml-[68px] -mt-1 text-[11px] text-[#C0392B] flex items-start gap-1.5" data-testid="compose-invalid-email-warning">
              <span className="font-bold">⚠</span>
              <span>
                This doesn&apos;t look like a valid email address (missing <code className="bg-[#FEF2F2] px-1">@</code> or domain).
                Update the contact&apos;s Email field on the Business Card.
              </span>
            </div>
          )}

          {/* Template picker (optional) */}
          {templates.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#666] w-[60px] shrink-0">Template</label>
              <select
                value={templateId}
                onChange={(e) => onTemplateChange(e.target.value)}
                className="flex-1 h-[30px] px-2 text-[12px] border border-[#B0B0B0] bg-white"
                data-testid="compose-template-select"
              >
                <option value="">— None (start blank) —</option>
                {templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Subject */}
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#666] w-[60px] shrink-0">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this email about?"
              className="flex-1 h-[30px] px-2 text-[12px] border border-[#B0B0B0]"
              data-testid="compose-subject"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#666] block mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Hi ${contact.first_name || 'there'},\n\n…`}
              rows={10}
              className="w-full px-3 py-2 text-[12px] border border-[#B0B0B0] resize-y leading-relaxed font-sans"
              data-testid="compose-body"
            />
            <p className="text-[10px] text-[#999] mt-1">
              Merge fields work: <code>{'{first_name}'}</code>, <code>{'{company}'}</code>, <code>{'{date}'}</code>, etc. Pick a template above to pre-fill.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 text-[12px] p-2" data-testid="compose-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#CCC] bg-[#F5F5F0] px-4 py-3 flex items-center justify-between gap-3 flex-wrap shrink-0">
          <div className="flex items-center gap-2 text-[11px] text-[#666]">
            <label htmlFor="compose-provider" className="font-semibold uppercase tracking-wide">Send via:</label>
            <select
              id="compose-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              disabled={sending}
              className="h-[26px] border border-[#B0B0B0] px-2 text-[11px] bg-white"
              data-testid="compose-provider-select"
            >
              {emailStatus.gmail_connected && (
                <option value="gmail">{`Gmail (${emailStatus.gmail_email || 'your Gmail'})`}</option>
              )}
              <option value="resend" disabled={!emailStatus.configured}>
                {`SalesCrunch system${emailStatus.sender ? ` (${emailStatus.sender})` : ''}${!emailStatus.configured ? ' — not configured' : ''}`}
              </option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={sending}
              className="text-[12px] bg-white border border-[#999] hover:bg-[#F0F0F0] disabled:opacity-50 text-[#333] font-semibold px-3 py-1.5"
              data-testid="compose-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={sending || !providerReady || !emailLooksValid}
              title={
                !emailLooksValid ? `Invalid email: ${contactEmail || '(blank)'}` :
                !providerReady ? (provider === 'gmail' ? 'Connect Gmail in Settings first' : 'Email service not configured') :
                ''
              }
              className="text-[12px] bg-[#F39C12] hover:bg-[#D68910] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold px-4 py-1.5 inline-flex items-center gap-2"
              data-testid="compose-send-btn"
            >
              {sending ? <><Spinner size={12} className="animate-spin" /> Sending…</> : <><PaperPlaneTilt size={12} weight="fill" /> Send Email</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
